/**
 * Environment Runs API
 *
 * Endpoints for managing and querying environment build runs.
 * Mirrors the verification runs pattern but scoped to the
 * environment canvas pipeline.
 */

import { apiClient } from './client';
import type {
  AgentRosterEntry,
  EnvironmentAttestation,
  EnvironmentRun,
  EnvironmentRunListResponse,
  EnvironmentRunFilters,
  EnvironmentRunModuleOutput,
  RunStep,
} from '../types/environmentRun';

// ── Create run ──────────────────────────────────────────────────────────

export async function createEnvironmentRun(
  theatreId: string,
): Promise<EnvironmentRun> {
  const { data } = await apiClient.post<EnvironmentRun>(
    '/api/v1/environment/runs',
    { theatre_id: theatreId },
  );
  return data;
}

// ── List runs ────────────────────────────────────────────────────────────

export async function fetchEnvironmentRuns(
  filters: EnvironmentRunFilters = {},
): Promise<EnvironmentRunListResponse> {
  const params: Record<string, string | number> = {};
  if (filters.theatre_id) params.theatre_id = filters.theatre_id;
  if (filters.status) params.status = filters.status;
  if (filters.construct_id) params.construct_id = filters.construct_id;
  if (filters.limit) params.limit = filters.limit;
  if (filters.offset) params.offset = filters.offset;

  const { data } = await apiClient.get<EnvironmentRunListResponse>(
    '/api/v1/environment/runs',
    { params },
  );
  return data;
}

// ── Single run detail ────────────────────────────────────────────────────

export async function fetchEnvironmentRun(
  runId: string,
): Promise<EnvironmentRun> {
  const { data } = await apiClient.get<EnvironmentRun>(
    `/api/v1/environment/runs/${runId}`,
  );
  return data;
}

// ── Attestation (proof artifact for CERTIFIED runs) ─────────────────────

export async function fetchEnvironmentAttestation(
  runId: string,
): Promise<EnvironmentAttestation> {
  const { data } = await apiClient.get<EnvironmentAttestation>(
    `/api/v1/environment/runs/${runId}/attestation`,
  );
  return data;
}

// ── Revoke attestation ───────────────────────────────────────────────────

export async function revokeEnvironmentAttestation(
  runId: string,
  reason: string,
): Promise<EnvironmentAttestation> {
  const { data } = await apiClient.post<EnvironmentAttestation>(
    `/api/v1/environment/runs/${runId}/attestation/revoke`,
    { reason },
  );
  return data;
}

// ── Run steps (stage-level breakdown) ────────────────────────────────────

export async function fetchRunSteps(
  runId: string,
): Promise<RunStep[]> {
  const { data } = await apiClient.get<RunStep[]>(
    `/api/v1/environment/runs/${runId}/steps`,
  );
  return data;
}

// ── Module outputs (per-module structured results) ──────────────────────

export async function fetchRunModuleOutputs(
  runId: string,
): Promise<EnvironmentRunModuleOutput[]> {
  const { data } = await apiClient.get<EnvironmentRunModuleOutput[]>(
    `/api/v1/environment/runs/${runId}/outputs`,
  );
  return data;
}

// ── Agent roster (for agent_actor binding) ───────────────────────────────

export async function fetchAgentRoster(): Promise<AgentRosterEntry[]> {
  const { data } = await apiClient.get<AgentRosterEntry[]>(
    '/api/v1/agents/roster',
  );
  return data;
}

// ── Cancel a run ─────────────────────────────────────────────────────────

export async function cancelEnvironmentRun(
  runId: string,
): Promise<EnvironmentRun> {
  const { data } = await apiClient.post<EnvironmentRun>(
    `/api/v1/environment/runs/${runId}/cancel`,
  );
  return data;
}

// ── Retry a failed run ───────────────────────────────────────────────────

export async function retryEnvironmentRun(
  runId: string,
): Promise<EnvironmentRun> {
  const { data } = await apiClient.post<EnvironmentRun>(
    `/api/v1/environment/runs/${runId}/retry`,
  );
  return data;
}
